import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react'
import { useDropzone, FileWithPath, FileRejection } from 'react-dropzone'
import makeAnimated from 'react-select/animated'
import Select from 'react-select'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import '@resources/css/request.css'
import '@resources/css/patient.css'
import '@resources/css/donator.css'
import '@resources/css/donators.css'
import { Step, Stepper } from 'react-form-stepper'
import ClinicDataStep from '@components/Request/ClinicDataStep'
import DonorValidationStep from '@components/Request/DonorValidationStep'
import CheckoutStep from '@components/Request/CheckoutStep'
import LoadingSpinner from '@components/LoadingSpinner/LoadingSpinner'

import Logotipo from '@resources/imgs/logo-proseed.webp'
import IDonor from '@interfaces/IDonor'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import AuthContext from '@contexts/Auth'
import * as $Order from '@services/Order'
import axios from 'axios'
import * as $TreatmentType from '@services/TreatmentType'
import * as $Clinic from '@services/Clinic'

const animatedComponents = makeAnimated()

function handleResize() {
  const elements = document.querySelectorAll('.dinamica')
  elements.forEach(element => {
    if (window.innerWidth === 430) {
      element.classList.add('col-6')
    } else {
      element.classList.remove('col-6')
    }
  })
}

type OptionType = { value: string; label: string }

interface PhotoMatchImage {
  id: string
  file: File
  preview: string
}

const Request: React.FC<any> = () => {
  const { user, cart, refetchUser, onRemoveDonorFromCart, onUpdateCart, onRequestReservation } = useContext(AuthContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [photomatchImages, setPhotomatchImages] = useState<PhotoMatchImage[]>([])
  const [dropzoneVisible, setDropzoneVisible] = useState<boolean>(false)
  const [photomatchPatientObservation, setPhotomatchPatientObservation] = useState<string>('')
  const [observation, setObservation] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedClinic, setSelectedClinic] = useState<OptionType | null>(null)

  const [ states, setStates ] = useState<any[]>([])
  const [ cities, setCities ] = useState<any[]>([])
  const [ clinics, setClinics ] = useState<any[]>([])

  const [ treatmentTypes, setTreatmentTypes ] = useState<any>([])

  const [donors, setDonors] = useState<IDonor[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [formData, setFormData] = useState<any>({
    clinic_state: '',
    clinic_city: '',
    clinic_id: '',

    doctor_name: '',
    doctor_crm: '',

    treatment_type_id: '',
    treatment_date: '',
  })

  // Get step from URL, default to 0
  const urlStepParam = searchParams.get('step')
  const urlStep = urlStepParam ? parseInt(urlStepParam, 10) : null
  const [step, setStepState] = useState<number>(urlStep !== null ? Math.max(0, Math.min(2, urlStep)) : 0)

  const updateStep = useCallback((nextStep: number) => {
    const normalizedStep = Math.max(0, Math.min(2, nextStep))
    setStepState(normalizedStep)
    setSearchParams({ step: String(normalizedStep) })
  }, [setSearchParams])

  const [selectedTreatment, setSelectedTreatment] = useState<string>('')

  const SweetAlert = withReactContent(Swal)

  // Refresh the cart from the API on mount so cart.internal_id is available for
  // saving between wizard steps (the context cart can be stale after SPA nav).
  useEffect(() => {
    refetchUser()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync step with URL parameter
  useEffect(() => {
    if (urlStep !== null) {
      const normalizedUrlStep = Math.max(0, Math.min(2, urlStep))
      if (normalizedUrlStep !== step) {
        setStepState(normalizedUrlStep)
      }
    }
  }, [urlStep, step])

  useEffect(() => {
    if (cart) {
      const newFormData = {
        ...formData,

        doctor_name: cart.doctor_name,
        doctor_crm: cart.doctor_crm,

        treatment_type_id: cart.treatment_type_id,
        treatment_date: cart.treatment_date,
      }

      if (cart.clinic && cart.clinic.main_address) {
        const address = cart.clinic.main_address

        newFormData.clinic_state = address.state
        newFormData.clinic_city = address.city
        newFormData.clinic_id = cart.clinic.id

        // Add small delay before setting selected clinic
        setTimeout(() => {
          setSelectedClinic({
            value: String(cart.clinic.id),
            label: cart.clinic.trading_name
          })
        }, 100) // 100ms delay
      }

      if (cart.treatment_type) {
        setSelectedTreatment(cart.treatment_type)
      }

      setDropzoneVisible(Boolean(cart.photo_match))
      setPhotomatchPatientObservation(cart.photomatch_patient_observation || '')
      setObservation(cart.observation || '')

      setFormData((prev: any) => ({ ...prev, ...newFormData }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart])

  // Prefill the clinic from the patient's registration/profile when the cart
  // has no clinic selected yet.
  useEffect(() => {
    const patient = user?.patient

    if (!patient?.clinic) return
    if (cart && cart.clinic && cart.clinic.id) return
    if (formData.clinic_id) return

    const address = patient.clinic.main_address || patient.clinic.addresses?.[0]

    if (!address || !address.state || !address.city) return

    setFormData((prev: any) => ({
      ...prev,
      clinic_state: address.state,
      clinic_city: address.city,
      clinic_id: patient.clinic!.id,
    }))

    setTimeout(() => {
      setSelectedClinic({
        value: String(patient.clinic!.id),
        label: patient.clinic!.trading_name,
      })
    }, 100)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cart])

  useEffect(() => {
    if (formData?.clinic_state) {
      $Clinic.cities(formData.clinic_state).then(({ data }) => setCities(data))
    }
  }, [formData?.clinic_state])

  useEffect(() => {
    if (formData?.clinic_city) {
      $Clinic.allByCity(formData.clinic_city).then(({ data }) => {
        setClinics(data.map((clinic: any) => ({
          value: clinic.id,
          label: clinic.name || clinic.trading_name,
        })))
      }).catch((error) => {
        console.error('Error fetching clinics:', error)
        setClinics([])
      })
    } else {
      setClinics([])
    }
    // Clear selected clinic when city changes
    setSelectedClinic(null)
    // Clear clinic from formData when city changes
    setFormData((prev: any) => ({ ...prev, clinic_id: '' }))
  }, [formData?.clinic_city])

  // Update formData when selectedClinic changes
  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      clinic_id: selectedClinic?.value || ''
    }))
  }, [selectedClinic])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      photomatchImages.forEach(img => URL.revokeObjectURL(img.preview))
    }
  }, [photomatchImages])

  useEffect(() => {
    setIsLoading(true)

    const $requests = [
      $Clinic.states(),
      $TreatmentType.all(),
      $Order.donors()
    ]

    axios.all($requests).then(axios.spread(({ data: states }, {data: treatmentTypes}, { data: donors }) => {
      setStates(states)

      setTreatmentTypes(treatmentTypes.map((treatmentType: any) => ({
        value: treatmentType.id,
        label: treatmentType.name
      })))

      setDonors(donors)

    })).catch((error) => {
      SweetAlert.fire('Erro', error.message || 'Erro ao buscar doadores do carrinho', 'error')
      navigate('/')
    }).finally(() => {
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true,
    onDrop: (acceptedFiles: FileWithPath[]) => handleDrop(acceptedFiles),
    onDropRejected: (fileRejections: FileRejection[]) => {
      const errors = fileRejections.map((rejection) => {
        const errorMessages = rejection.errors.map((e) => {
          if (e.code === 'file-too-large') return `"${rejection.file.name}" excede o tamanho máximo de 10MB.`
          if (e.code === 'file-invalid-type') return `"${rejection.file.name}" não é um formato válido. Use PNG ou JPEG.`
          return e.message
        })
        return errorMessages.join(' ')
      })
      SweetAlert.fire('Arquivo inválido', errors.join('<br/>'), 'error')
    },
  })

  const handleDrop = (acceptedFiles: FileWithPath[]) => {
    const remainingSlots = 4 - photomatchImages.length
    const filesToAdd = acceptedFiles.slice(0, remainingSlots)

    if (acceptedFiles.length > remainingSlots) {
      SweetAlert.fire('Atenção', 'Você pode enviar no máximo 4 imagens.', 'warning')
    }

    if (filesToAdd.length > 0) {
      const newImages = filesToAdd.map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file)
      }))
      setPhotomatchImages([...photomatchImages, ...newImages])
    }
  }

  const handleCheckboxChange = (checked: boolean) => {
    setDropzoneVisible(checked)

    if (!checked) {
      photomatchImages.forEach(img => URL.revokeObjectURL(img.preview))
      setPhotomatchImages([])
      setPhotomatchPatientObservation('')
    }
  }

  const handleRemoveImage = (id: string) => {
    const imageToRemove = photomatchImages.find(img => img.id === id)
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.preview)
    }

    setPhotomatchImages(photomatchImages.filter(img => img.id !== id))
  }

  const handleRemoveFromCart = (id: string) => {
    onRemoveDonorFromCart(id).then(() => {
      setDonors((prevDonors) => prevDonors.filter((donor) => donor.internal_id !== id))
    })
  }

  const handleRefreshDonors = () => {
    setIsLoading(true)
    $Order.donors().then(({ data: donors }) => {
      setDonors(donors)
    }).catch((error) => {
      console.error('Error refreshing donors:', error)
    }).finally(() => {
      setIsLoading(false)
    })
  }

  const handleSelectChange = useCallback((key: string, option: any) => {
    const { value } = option

    if (key === 'treatment_type_id') {
      setSelectedTreatment(option.label)
    }

    return setFormData((formData: any) => {
      return {
        ...formData,
        [key]: value,
      }
    })
  }, [setFormData])

  const handleInputChange = useCallback((key: string, value: string) => setFormData((formData: any) => {
    return {
      ...formData,
      [key]: value,
    }
  }), [setFormData])

  const handleFinishCheckout = async () => {
    if (!cart?.internal_id) {
      SweetAlert.fire('Erro', 'Carrinho inválido para concluir a solicitação.', 'error')
      return
    }

    // Validate PhotoMatching images if service is enabled
    if (dropzoneVisible && photomatchImages.length < 2) {
      SweetAlert.fire('Atenção', 'Para utilizar o serviço de Photomatching, é necessário enviar no mínimo 2 fotos.', 'warning')
      return
    }

    const formData = new FormData()
    formData.append('photo_match', dropzoneVisible ? '1' : '0')
    formData.append('observation', observation)
    formData.append('photomatch_patient_observation', photomatchPatientObservation)

    // Add photomatch files if any
    if (photomatchImages.length > 0) {
      photomatchImages.forEach((img, index) => {
        formData.append(`photomatch_files[${index + 1}]`, img.file)
      })
    }

    try {
      setIsSubmitting(true)
      await onRequestReservation(cart.internal_id, formData)
      navigate('/pedidos')
    } catch (error) {
      console.error('Erro ao finalizar checkout:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Validation functions
  const validateStep1 = useCallback((data?: any, clinic?: OptionType | null) => {
    const currentData = data ?? formData
    const currentClinic = clinic ?? selectedClinic
    const requiredFields = ['clinic_state', 'clinic_city', 'treatment_type_id', 'treatment_date']
    const hasClinic = currentClinic !== null
    const hasAllFields = requiredFields.every(field => currentData[field] && String(currentData[field]).trim() !== '')
    return hasClinic && hasAllFields
  }, [formData, selectedClinic])

  const validateStep2 = useCallback((donorList?: IDonor[]) => {
    const currentDonors = donorList ?? donors
    return currentDonors.length >= 1
  }, [donors])

  const canProceedToStep = (targetStep: number) => {
    switch (targetStep) {
    case 1:
      return validateStep1()
    case 2:
      return validateStep2()
    default:
      return true
    }
  }

  const handleStepNavigation = async (targetStep: number) => {
    if (canProceedToStep(targetStep)) {
      // The cart is resolved server-side, so we no longer need its internal_id
      // here — just persist the current step data before advancing.
      if (targetStep !== 0) {
        await onUpdateCart(formData)
      }

      updateStep(targetStep)
    } else {
      let message = ''
      if (targetStep === 1 && !validateStep1()) {
        message = 'Por favor, preencha todos os campos obrigatórios da primeira etapa antes de continuar.'
      } else if (targetStep === 2 && !validateStep2()) {
        message = 'Por favor, selecione pelo menos 1 doador antes de continuar para o checkout.'
      }
      if (message) {
        SweetAlert.fire('Atenção', message, 'warning')
      }
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const hasDonorWithoutEligibleTreatment = useMemo(() => {
    return donors.some((donor) => {
      const availableTreatments = donor?.available_treatment_names ?? []
      return selectedTreatment && !availableTreatments.includes(selectedTreatment === 'Inseminação' ? 'IIU' : selectedTreatment)
    })
  }, [donors, selectedTreatment])

  const City = useCallback(() => {
    return (
      <Select
        className="react-select"
        placeholder={<div>Selecione</div>}
        components={animatedComponents}
        options={cities}
        value={formData ? cities.find(s => s.value === formData?.clinic_city) : null}
        defaultValue={cities.find(s => s.value === formData?.clinic_city)}
        onChange={value => value ? handleSelectChange('clinic_city', value) : null}
        isClearable={true}
        isSearchable={true}
        isDisabled={!formData?.clinic_state}
        isLoading={false}
        isRtl={false}
        closeMenuOnSelect={true}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 1000 })
        }}
      />
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.clinic_city, cities, handleSelectChange])

  return (
    <section className="steps-area">
      <div className="container-fluid mt-5 mb-3 d-flex justify-content-center align-items-center">
        <Link className="link" to="/carrinho">
          <img src={Logotipo} alt="ProSeed" className="logo" width="300" />
        </Link>
      </div>
      <>
        <LoadingSpinner isLoading={isLoading} text="Carregando dados da solicitação..." />
        <div className="container-fluid">
          <Stepper activeStep={step} className="mb-3 align-items-stretch">
            <Step label="Clínica de Tratamento" completed={step > 0} onClick={() => updateStep(0)} active={step === 0} />
            <Step label="Validação de Doadores" completed={step > 1} onClick={() => handleStepNavigation(1)} active={step === 1} />
            <Step label="Checkout" completed={step > 2} onClick={() => handleStepNavigation(2)} active={step === 2} />
          </Stepper>
        </div>

        <div className="container-fluid">
          {step === 0 && (
            <ClinicDataStep
              states={states}
              clinics={clinics}
              treatmentTypes={treatmentTypes}
              formData={formData}
              selectedClinic={selectedClinic}
              selectedTreatment={selectedTreatment}
              handleSelectChange={handleSelectChange}
              handleInputChange={handleInputChange}
              setSelectedClinic={setSelectedClinic}
              City={City}
              onNext={() => handleStepNavigation(1)}
            />
          )}

          {step === 1 && (
            <DonorValidationStep
              donors={donors}
              setDonors={setDonors}
              selectedTreatment={selectedTreatment}
              handleRemoveFromCart={handleRemoveFromCart}
              hasDonorWithoutEligibleTreatment={hasDonorWithoutEligibleTreatment}
              onBack={() => updateStep(0)}
              onNext={() => handleStepNavigation(2)}
              onRefreshDonors={handleRefreshDonors}
            />
          )}

          {step === 2 && (
            <CheckoutStep
              formData={formData}
              selectedTreatment={selectedTreatment}
              photomatchImages={photomatchImages}
              dropzoneVisible={dropzoneVisible}
              handleCheckboxChange={handleCheckboxChange}
              handleRemoveImage={handleRemoveImage}
              handleDrop={handleDrop}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              onBack={() => updateStep(1)}
              onFinishCheckout={handleFinishCheckout}
              onEditClinic={() => updateStep(0)}
              onEditDonors={() => updateStep(1)}
              photomatchPatientObservation={photomatchPatientObservation}
              observation={observation}
              onPhotomatchPatientObservationChange={setPhotomatchPatientObservation}
              onObservationChange={setObservation}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </>
    </section>
  )
}

export default Request
